/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useState } from 'react';
import { Check, Plus, Users, X } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  CIRCLES_CHANGED_TEXT,
  CIRCLES_HINT,
  CIRCLES_SAVED_TEXT,
  CIRCLES_UNREADABLE_TEXT,
  circleGroupAddRefusal,
  circleGroupIdOf,
  circleGroupText,
  circlesChanged,
  circlesSaveBody,
  type CirclesView,
} from '../lib/pcSwitches';
import { Button, Callout, Field, Icon, Input, Panel, Skeleton } from '../ui';
import { SaveNote } from './SaveNote';

export interface EarlyCircleSectionProps {
  /** Writes are off: the admin is an auditor, or the page is busy. */
  locked: boolean;
}

type Read =
  | { status: 'loading' }
  /** A service without the route (an older one): the section is not shown. */
  | { status: 'absent' }
  | { status: 'failed'; text: string }
  | { status: 'loaded'; view: CirclesView };

/**
 * "Early circle" under PC switches › For everyone › Updates (owner, 2026-10-27: an Early circle of "people in security
 * group USER-SOFTWAREDEV and USER-QA plus any others i want to add manually from gyro-admin interface"): the Entra groups
 * whose members get Early releases, each with whether it is in effect now (it waits for The Bridge connection for
 * gyro-svc). One Save sends the whole list with the revision the section loaded; the people added one at a time are the
 * Update circle exceptions under Exceptions for people. The section reads its own setting, and hides on a service without it.
 */
export function EarlyCircleSection({ locked }: EarlyCircleSectionProps) {
  const [read, setRead] = useState<Read>({ status: 'loading' });
  const [groups, setGroups] = useState<string[]>([]);
  const [newGroup, setNewGroup] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  /** Show what the service holds: the groups as stored. */
  const show = useCallback((view: CirclesView) => {
    setRead({ status: 'loaded', view });
    setGroups(view.earlyGroups.map((group) => group.groupId));
  }, []);

  useEffect(() => {
    let live = true;
    api.desktopUpdateCircles().then(
      (view) => {
        if (!live) return;
        if (view === null) setRead({ status: 'absent' });
        else show(view);
      },
      (error: unknown) => {
        if (live) setRead({ status: 'failed', text: errorText(error, CIRCLES_UNREADABLE_TEXT) });
      },
    );
    return () => {
      live = false;
    };
  }, [show]);

  if (read.status === 'absent') return null;
  const view = read.status === 'loaded' ? read.view : null;
  const disabled = locked || busy || view === null;
  const dirty = view !== null && circlesChanged(groups, view);
  const addRefusal = newGroup.trim() === '' || view === null ? null : circleGroupAddRefusal(newGroup, groups, view.limits.earlyGroups);
  const canAdd = !disabled && newGroup.trim() !== '' && addRefusal === null;

  const add = () => {
    if (!canAdd) return;
    setGroups((current) => [...current, circleGroupIdOf(newGroup)]);
    setNewGroup('');
    setNote(null);
  };
  const cancel = () => {
    if (!view) return;
    setGroups(view.earlyGroups.map((group) => group.groupId));
    setNote(null);
  };
  const save = async () => {
    if (!view) return;
    setBusy(true);
    setNote(null);
    try {
      show(await api.saveDesktopUpdateCircles(circlesSaveBody(groups, view.revision)));
      setNote({ ok: true, text: CIRCLES_SAVED_TEXT });
    } catch (error) {
      const code = error instanceof ApiError && typeof error.body?.error === 'string' ? error.body.error : undefined;
      if (code === 'update_circles_changed') {
        setNote({ ok: false, text: CIRCLES_CHANGED_TEXT });
        try {
          const fresh = await api.desktopUpdateCircles();
          if (fresh) show(fresh);
        } catch {
          // The note already says to check it again; a reload shows it.
        }
      } else {
        setNote({ ok: false, text: errorText(error, 'Nothing was saved.') });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      heading="h3"
      titleId="early-circle-title"
      title={<span className="ui-title-icon"><Icon icon={Users} size="md" />Early circle</span>}
      description={CIRCLES_HINT}
    >
      {read.status === 'loading' && <div className="ui-form" aria-hidden="true"><Skeleton size="md" width="240px" /><Skeleton size="lg" /></div>}
      {read.status === 'failed' && <Callout tone="warn" role="alert">{read.text}</Callout>}
      {view && (
        <fieldset disabled={disabled} className="ui-fieldset">
          <div className="ui-form">
            <Field label="Groups">
              {groups.length === 0 ? (
                <p className="ui-cell-sub">No group is listed: only people with their own Update circle exception get Early.</p>
              ) : (
                <ul aria-label="Early circle groups" className="ui-people-list">
                  {groups.map((groupId) => {
                    const status = view.earlyGroups.find((group) => group.groupId === groupId);
                    return (
                      <li key={groupId} data-group={groupId}>
                        <span className="ui-cell-stack">
                          <span className="is-mono">{groupId}</span>
                          <span className={status ? (status.inEffect ? 'ui-cell-sub ui-text-ok' : 'ui-cell-sub ui-text-warn') : 'ui-cell-sub'} data-group-state={status?.state ?? 'unsaved'}>
                            {status ? circleGroupText(status) : 'Not saved yet.'}
                          </span>
                        </span>
                        <Button variant="ghost" size="sm" icon={X} aria-label={`Remove group ${groupId}`} onClick={() => { setGroups((current) => current.filter((known) => known !== groupId)); setNote(null); }}>Remove</Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Field>
            <Field label="Add a group (object id)" help="The Entra security group's object id, from Entra admin center › Groups." error={addRefusal ?? undefined}>
              <div className="ui-actions">
                <Input
                  id="early-circle-group"
                  className="is-mono ui-w-lg"
                  placeholder="00000000-0000-0000-0000-000000000000"
                  value={newGroup}
                  onChange={(e) => setNewGroup(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                />
                <Button size="sm" icon={Plus} disabled={!canAdd} onClick={add}>Add</Button>
              </div>
            </Field>
            <div className="ui-actions">
              <Button variant="primary" size="sm" icon={Check} busy={busy} disabled={!dirty || disabled} onClick={() => void save()}>Save</Button>
              <Button variant="ghost" size="sm" icon={X} disabled={!dirty || disabled} onClick={cancel}>Cancel</Button>
              {dirty && <span className="ui-cell-sub">Not saved yet.</span>}
            </div>
          </div>
        </fieldset>
      )}
      <SaveNote note={note} />
    </Panel>
  );
}
